/**
 * TASK-053 — shared upload progress surface.
 *
 * Before this, uploading media gave no feedback at all: no sign that background
 * work started, no per-file progress, and a failed file just vanished. One hook
 * + one panel, used by every upload call site, so the experience is identical
 * in the Media library, the New Post picker and the post editor.
 *
 * Design notes:
 * - Progress is REAL bytes (api.uploadWithProgress → XHR upload events), not a
 *   spinner. When the browser reports a non-computable length we show the bytes
 *   sent instead of a fake percentage — never a bar that lies.
 * - The panel is non-modal and dismissible: uploads continue in the background,
 *   which is the point of the request ("покажи, что идёт фоновая работа").
 * - A failed file STAYS in the list with its error and a Retry button; the
 *   caller supplies the retry by re-running the same upload function.
 */
import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as I from './Icons.jsx';

const UploadProgressContext = createContext(null);

function formatBytes(bytes) {
  if (!Number.isFinite(bytes) || bytes < 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function UploadProgressProvider({ children }) {
  const [items, setItems] = useState([]);
  const [dismissed, setDismissed] = useState(false);
  const seq = useRef(0);

  const update = useCallback((id, patch) => {
    setItems((prev) => prev.map((item) => (item.id === id ? { ...item, ...patch } : item)));
  }, []);

  /**
   * Track one upload. `run(onProgress)` must perform the upload and resolve/reject.
   * Returns the upload's promise so callers keep their existing control flow.
   */
  const track = useCallback(async (name, run, { size = null } = {}) => {
    seq.current += 1;
    const id = `up_${seq.current}_${name}`;
    setDismissed(false);
    setItems((prev) => [...prev, { id, name, size, loaded: 0, ratio: null, status: 'uploading', error: null, run }]);
    try {
      const result = await run((progress) => {
        update(id, {
          loaded: progress?.loaded ?? 0,
          ratio: progress?.ratio ?? null,
          // A ratio of 1 means bytes are out; the server is still working.
          status: progress?.ratio === 1 ? 'processing' : 'uploading'
        });
      });
      update(id, { status: 'done', ratio: 1, error: null });
      // Completed rows are transient — failures are not.
      setTimeout(() => setItems((prev) => prev.filter((item) => item.id !== id)), 4000);
      return result;
    } catch (error) {
      const cancelled = error?.code === 'CANCELLED';
      update(id, { status: cancelled ? 'cancelled' : 'failed', error: error?.message || String(error) });
      throw error;
    }
  }, [update]);

  const retry = useCallback((id) => {
    const item = items.find((entry) => entry.id === id);
    if (!item?.run) return;
    setItems((prev) => prev.filter((entry) => entry.id !== id));
    track(item.name, item.run, { size: item.size }).catch(() => { /* surfaced in the panel */ });
  }, [items, track]);

  const remove = useCallback((id) => setItems((prev) => prev.filter((item) => item.id !== id)), []);

  const value = useMemo(() => ({ items, track, retry, remove, dismiss: () => setDismissed(true) }),
    [items, track, retry, remove]);

  return (
    <UploadProgressContext.Provider value={value}>
      {children}
      {!dismissed && items.length > 0 && <UploadProgressPanel />}
    </UploadProgressContext.Provider>
  );
}

export function useUploadProgress() {
  const ctx = useContext(UploadProgressContext);
  // Call sites must keep working even outside the provider (tests, isolated
  // screens): fall back to running the upload untracked rather than throwing.
  return ctx ?? {
    items: [],
    track: (_name, run) => run(() => {}),
    retry: () => {},
    remove: () => {},
    dismiss: () => {}
  };
}

function UploadProgressPanel() {
  const { t } = useTranslation();
  const { items, retry, remove, dismiss } = useUploadProgress();
  const active = items.filter((item) => item.status === 'uploading' || item.status === 'processing').length;

  return (
    <div className="upload-progress-panel" role="status" aria-live="polite">
      <div className="upload-progress-head">
        <strong>{active > 0 ? t('uploads.inProgress', { count: active }) : t('uploads.finished')}</strong>
        <button type="button" className="btn btn-ghost btn-sm" onClick={dismiss} aria-label={t('uploads.hide')}>
          <I.Icon name="x" size={12} />
        </button>
      </div>
      <p className="upload-progress-hint">{t('uploads.keepWorking')}</p>
      <ul className="upload-progress-list">
        {items.map((item) => (
          <li key={item.id} className={`upload-progress-item is-${item.status}`}>
            <div className="upload-progress-row">
              <span className="upload-progress-name" title={item.name}>{item.name}</span>
              <span className="upload-progress-meta">
                {item.status === 'done' && t('uploads.done')}
                {item.status === 'failed' && t('uploads.failed')}
                {item.status === 'cancelled' && t('uploads.cancelled')}
                {item.status === 'processing' && t('uploads.processing')}
                {item.status === 'uploading' && (
                  item.ratio != null
                    ? `${Math.round(item.ratio * 100)}%`
                    : formatBytes(item.loaded)
                )}
              </span>
            </div>
            {(item.status === 'uploading' || item.status === 'processing') && (
              <div className="upload-progress-bar">
                <div
                  className={`upload-progress-fill ${item.ratio == null ? 'is-indeterminate' : ''}`}
                  style={item.ratio == null ? undefined : { width: `${Math.round(item.ratio * 100)}%` }}
                />
              </div>
            )}
            {item.status === 'failed' && (
              <div className="upload-progress-error">
                <span>{item.error}</span>
                <span className="upload-progress-actions">
                  <button type="button" className="btn btn-ghost btn-sm" onClick={() => retry(item.id)}>
                    {t('uploads.retry')}
                  </button>
                  <button type="button" className="btn btn-ghost btn-sm" onClick={() => remove(item.id)}>
                    {t('uploads.dismissFile')}
                  </button>
                </span>
              </div>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

export default UploadProgressProvider;
